import React, { useEffect, useRef, useState } from "react";
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export type SnackbarType = "success" | "error" | "warning" | "info";

export interface SnackbarState {
  id: string;
  message: string;
  type: SnackbarType;
  // no duration in state — duration is a prop on the item
}

interface SnackbarItemProps {
  snackbar: SnackbarState;
  onClose: (id: string) => void;
  /**
   * Auto-dismiss duration in milliseconds. Default 8000 (8s).
   * If set to 0 or negative, the snackbar will be persistent until manually closed.
   */
  duration?: number;
  /**
   * Optionally allow programmatic immediateClose (skips exit animation).
   */
  immediateClose?: boolean;
}

const SnackbarItem: React.FC<SnackbarItemProps> = ({
  snackbar,
  onClose,
  duration = 8000,
  immediateClose = false,
}) => {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);

  // Refs to manage timer and remaining time for pause/resume
  const timerRef = useRef<number | null>(null);
  const startRef = useRef<number | null>(null);
  const remainingRef = useRef<number>(duration);
  const isPausedRef = useRef(false);

  useEffect(() => {
    const t = window.setTimeout(() => setVisible(true), 30);
    return () => clearTimeout(t);
  }, []);

  // Setup auto-dismiss timer
  useEffect(() => {
    // Clear any existing timer first
    clearTimer();

    // If duration is not positive, don't set auto-dismiss
    if (duration <= 0) return;

    // Start timer
    startTimer(remainingRef.current);

    return () => {
      clearTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snackbar.id, duration]);

  const clearTimer = () => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    startRef.current = null;
  };

  const startTimer = (ms: number) => {
    clearTimer();
    if (ms <= 0) return;
    startRef.current = Date.now();
    // window.setTimeout returns number in browser
    timerRef.current = window.setTimeout(() => {
      // When timeout fires, trigger close animation
      triggerClose();
    }, ms);
  };

  const pauseTimer = () => {
    if (duration <= 0) return;
    if (isPausedRef.current) return;
    if (timerRef.current == null || startRef.current == null) {
      isPausedRef.current = true;
      return;
    }
    const elapsed = Date.now() - startRef.current;
    remainingRef.current = Math.max(0, remainingRef.current - elapsed);
    clearTimer();
    isPausedRef.current = true;
  };

  const resumeTimer = () => {
    if (duration <= 0) return;
    if (!isPausedRef.current) return;
    isPausedRef.current = false;
    // If no remaining time, close immediately
    if (remainingRef.current <= 0) {
      triggerClose();
      return;
    }
    startTimer(remainingRef.current);
  };

  // Called to start exit + final onClose after animation
  const triggerClose = () => {
    // If immediateClose requested, call onClose right away (skip exit animation)
    if (immediateClose) {
      clearTimer();
      onClose(snackbar.id);
      return;
    }

    // otherwise, run exit animation then call onClose
    setExiting(true);
    // match CSS transition duration (200ms)
    setTimeout(() => {
      onClose(snackbar.id);
    }, 200);
  };

  // user-initiated close
  const handleClose = () => {
    clearTimer();
    triggerClose();
  };

  const renderIcon = () => {
    const cls = "w-5 h-5 drop-shadow-sm";
    switch (snackbar.type) {
      case "success":
        return <CheckCircle className={cls} />;
      case "error":
        return <AlertCircle className={cls} />;
      case "warning":
        return <AlertTriangle className={cls} />;
      case "info":
      default:
        return <Info className={cls} />;
    }
  };

  const bgClasses = (() => {
    switch (snackbar.type) {
      case "success":
        return "bg-gradient-to-r from-emerald-500 via-green-500 to-emerald-600 border-emerald-400 shadow-emerald-500/25";
      case "error":
        return "bg-gradient-to-r from-red-500 via-rose-500 to-red-600 border-red-400 shadow-red-500/25";
      case "warning":
        return "bg-gradient-to-r from-amber-400 via-yellow-500 to-orange-500 border-amber-300 shadow-amber-500/25";
      case "info":
      default:
        return "bg-gradient-to-r from-blue-500 via-cyan-500 to-blue-600 border-blue-400 shadow-blue-500/25";
    }
  })();

  // Pause on hover / mouse down; resume on leave / mouse up
  return (
    <div
      role="status"
      aria-live="polite"
      onMouseEnter={pauseTimer}
      onMouseLeave={resumeTimer}
      onMouseDown={pauseTimer}
      onMouseUp={resumeTimer}
      // Touch support: pause on touch start, resume on touch end
      onTouchStart={pauseTimer}
      onTouchEnd={resumeTimer}
      className={`
        relative flex items-center justify-between p-4 rounded-xl shadow-xl min-w-[280px] max-w-md
        border border-white/10 backdrop-blur-sm transition-all duration-200 ease-out transform-gpu
        ${bgClasses} text-white
        ${visible && !exiting ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}
      `}
      style={{ zIndex: 99999, pointerEvents: "auto" }}
    >
      <div className="relative flex items-center gap-3 z-10">
        <div className="flex-shrink-0 p-1 rounded-full bg-white/20 backdrop-blur-sm">
          {renderIcon()}
        </div>
        <span className="font-medium text-sm leading-relaxed drop-shadow-sm">
          {snackbar.message}
        </span>
      </div>

      <button
        onClick={handleClose}
        className="ml-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-all duration-150 active:scale-95 backdrop-blur-sm"
        aria-label="Close notification"
      >
        <X className="w-4 h-4 drop-shadow-sm" />
      </button>
    </div>
  );
};

export default SnackbarItem;
